import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { type CorporationListState } from "./useCorporationList";

interface Props {
  open: boolean;
  state: CorporationListState;
  count: number;
  disabled: boolean;
  onClose: () => void;
}

/**
 * 取得内容と最大の消費クレジットを確認して、取得を依頼する。
 * 誤タップで閉じないよう、背景タップ・Escでは閉じない（レスポンシブ基盤ガイド）
 */
export const AcquisitionConfirmDialog = ({
  open,
  state,
  count,
  disabled,
  onClose,
}: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const tapSx = isMobile ? { minHeight: MIN_TAP_TARGET } : undefined;
  return (
    <Dialog open={open} fullScreen={isMobile} fullWidth maxWidth="sm">
      <DialogTitle>商業登記の取得内容を確認</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            全部事項の登記情報 / 現在の登記 / {count.toLocaleString()}法人
          </Typography>
          <Typography>
            1件 {state.settings?.unitPrice.toLocaleString()}クレジット ×{" "}
            {count.toLocaleString()}件
          </Typography>
          <Typography variant="h6">
            合計 最大{" "}
            {((state.settings?.unitPrice ?? 0) * count).toLocaleString()}
            クレジット
          </Typography>
          <Alert severity="info">
            取得に成功した件数分のみ課金されます。取得した商業登記の解析は無料で行います。閉鎖登記は取得しません。
          </Alert>
          {state.pending && (
            <Alert severity="warning">
              前回と同じ受付キーで送信します。送信内容を変更した場合、すでに受付済みの依頼とは別の依頼を作成できません。
            </Alert>
          )}
          {state.error && <Alert severity="error">{state.error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={state.sending} sx={tapSx} onClick={onClose}>
          戻る
        </Button>
        <Button
          variant="contained"
          disabled={
            disabled ||
            state.matchingRequired ||
            !state.settings?.acquisitionEnabled ||
            !count
          }
          sx={tapSx}
          onClick={() => {
            void state.submit();
          }}
        >
          {state.sending ? "送信中" : "この内容で取得を依頼"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
